<script setup lang="ts">
/**
 * The frame every landing section shares: a brass rim along its top edge (the
 * in-game top bar's), an optional dimmed painting behind it that drifts with
 * the scroll (`--py`, set by `parallax.ts` on `[data-parallax]`), the title
 * plate with one line under it, and the centred column the content sits in.
 *
 * Owns: the section's chrome and the `data-section` anchor the top bar scrolls
 * to. Must not: know what a section shows.
 */
import GameIcon from '@/ui/kit/GameIcon.vue';
import type { GameIconName } from '@/ui/kit/icons';

defineProps<{
  id: string;
  icon: GameIconName;
  title: string;
  line?: string;
  /** A painting under the veil, lazy-loaded (below the fold by construction). */
  backdrop?: string;
  /** `wide` lets rows of cards run to the viewport edge. */
  wide?: boolean;
}>();
</script>

<template>
  <section class="shell" :data-section="id" :aria-labelledby="`${id}-title`" data-parallax>
    <div class="k-girder rim" aria-hidden="true"></div>
    <img v-if="backdrop" class="backdrop" :src="backdrop" alt="" loading="lazy" decoding="async" />
    <div class="veil" aria-hidden="true"></div>
    <div class="column" :class="{ wide }">
      <header class="head" data-slam>
        <div class="k-header plate">
          <GameIcon :name="icon" :size="56" />
          <h2 :id="`${id}-title`" class="k-title">{{ title }}</h2>
        </div>
        <p v-if="line" class="line">{{ line }}</p>
      </header>
      <slot />
    </div>
  </section>
</template>

<style scoped>
.shell {
  position: relative;
  isolation: isolate;
  /* `clip`, not `hidden`: hidden makes the section a scroll container and a sticky child (the ranks' captain) would stop sticking. */
  overflow: clip;
  padding: clamp(56px, 9vw, 112px) 0 clamp(48px, 8vw, 96px);
  background: #0a1119;
  scroll-margin-top: 64px;
}

/* The riveted girder (`.k-girder`) that divides every section from the one above. */
.rim {
  position: absolute;
  inset: 0 0 auto 0;
  z-index: 1;
}

.backdrop {
  position: absolute;
  inset: -8% 0;
  z-index: -2;
  width: 100%;
  height: 116%;
  object-fit: cover;
  opacity: 0.85;
  transform: translateY(calc(var(--py, 0) * -6%));
  will-change: transform;
}

.veil {
  position: absolute;
  inset: 0;
  z-index: -1;
  background: linear-gradient(180deg, rgba(8, 14, 21, 0.7), rgba(8, 14, 21, 0.35) 45%, rgba(8, 14, 21, 0.78));
}

.column {
  width: min(1180px, 100% - 2 * clamp(14px, 4vw, 40px));
  margin: 0 auto;
}

.column.wide {
  width: min(1320px, 100%);
}

.head {
  display: grid;
  justify-items: center;
  gap: 12px;
  margin-bottom: clamp(22px, 4vw, 44px);
  text-align: center;
}

.plate {
  --h: clamp(60px, 8vw, 84px);
  max-width: calc(100% - 8px);
  gap: 0.4rem;
  padding-inline: clamp(28px, 7vw, 62px);
}

.plate .k-title {
  font-size: clamp(1.3rem, 0.9rem + 2.6vw, 2.8rem);
  text-transform: uppercase;
  text-align: center;
  line-height: 1.02;
  text-wrap: balance;
}

.line {
  margin: 0;
  max-width: 34rem;
  padding: 0 14px;
  font-family: var(--font-game-body);
  font-weight: 600;
  font-size: clamp(0.95rem, 0.85rem + 0.5vw, 1.2rem);
  color: var(--kit-cream);
  text-shadow: 0 2px 0 var(--kit-ink), 0 0 12px rgba(13, 20, 28, 0.9);
}

/* A phone on its side: every pixel of height is the page's, so the plate and the gaps tighten. */
@media (max-height: 520px) {
  .shell {
    padding: 36px 0 30px;
  }

  .plate {
    --h: 52px;
  }

  .head {
    gap: 6px;
    margin-bottom: 14px;
  }
}
</style>
